/**
 * THE RECORD, AS THE CONTRACT STATES IT.
 *
 * Every field here is one the document contract actually returns
 * (backend/schemas/document.py::DocumentResponse). Nothing is derived from a
 * filename, a similar title, an upload date or proximity in the UI.
 *
 * WHAT IS DELIBERATELY ABSENT — and why:
 *
 *   LINKED REQUIREMENTS. `ProjectRequirement.source_document_id` exists in
 *   the backend model, and `_detail()` returns it as `sourceDocumentId`. But
 *   the LIST contract `GET /requirements/projects/{id}` returns `_summary()`,
 *   which carries only `hasCitation` — not the document id — and takes no
 *   document filter. Proving "these requirements cite THIS document" would
 *   mean fetching every requirement's detail and joining client-side, on a
 *   surface that is additionally behind a tenant entitlement, a feature flag
 *   and a capability. That is not a lawful frontend-readable linkage, so the
 *   panel is not rendered. The ask is recorded in
 *   docs/OMNAS_DRAWING_WORKSPACE_1_BACKEND_ASKS.md.
 *
 *   RFI / submittal / activity / procurement / observation / model-element
 *   links, sheet numbers, disciplines and revision lineage. No contract on
 *   current main returns any of them against a document. A disabled control
 *   would be a claim that the capability is nearly here; an absent one is the
 *   truth.
 *
 * THIS MODULE IMPORTS NO RADIX PRIMITIVE, on purpose. `@radix-ui/react-select`
 * reaches a subpath (`@radix-ui/primitive/is-development`) that Jest's
 * resolver cannot follow, so anything importing it is unrenderable in a unit
 * test. Keeping every CLAIM in a radix-free module is what lets the claims be
 * asserted against real markup instead of taken on trust; the three form
 * widgets that do need radix live next door in DocumentContextPanel.
 */
import { Button } from '../../components/ui/button';
import { Download, ExternalLink, Check, X, Circle } from 'lucide-react';
import { formatTimestamp } from './documentPresentation';
import { DocumentTypeIcon } from './DocumentPreview';
import { StatusMark } from './DocumentBrowser';

const Field = ({ label, children, mono = false }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <dt className="shrink-0 text-[0.7rem] uppercase tracking-wide text-muted-foreground">
      {label}
    </dt>
    <dd
      className={`min-w-0 flex-1 text-right text-sm text-foreground ${mono ? 'font-mono text-xs' : ''}`}
    >
      {children}
    </dd>
  </div>
);

/**
 * THE FOUR CLAIMS, DRAWN SEPARATELY.
 *
 * This is the block the whole feature exists to make possible. A person can
 * see that the record exists, that storage holds a file, that a link was or
 * was not issued, and whether the format is one we render — four answers, not
 * one green tick or one red cross. When OMNΛS becomes a visual intelligence
 * platform this is the distinction that stops "we cannot show it" from being
 * heard as "it is not there".
 */
const Claim = ({ established, label, note }) => {
  const Icon = established ? Check : X;
  return (
    <li className="flex items-start gap-2 py-1">
      <Icon
        className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${established ? 'text-emerald-400' : 'text-muted-foreground'}`}
        aria-hidden="true"
      />
      <span className="min-w-0 text-xs">
        <span className="text-foreground">{label}</span>
        <span className="sr-only">
          {established ? ': established' : ': not established'}
        </span>
        {note && (
          <span className="block text-[0.7rem] text-muted-foreground">
            {note}
          </span>
        )}
      </span>
    </li>
  );
};

export const AvailabilityBreakdown = ({ availability }) => (
  <div
    data-testid="document-availability-breakdown"
    data-availability={availability.state}
  >
    <p className="mb-1 text-[0.7rem] uppercase tracking-wide text-muted-foreground">
      What is established
    </p>
    <ul className="rounded-md border border-border/60 bg-muted/20 px-3 py-1.5">
      <Claim
        established={availability.recordExists}
        label="The document record exists"
      />
      <Claim
        established={availability.storageHoldsFile}
        label="Object storage holds a file for it"
        note={
          availability.storageHoldsFile
            ? null
            : 'Nothing was ever stored against this record.'
        }
      />
      <Claim
        established={availability.signedUrlIssued}
        label="A temporary download link was issued"
        note={
          availability.signedUrlIssued
            ? null
            : 'The link is absent from this response. The file is not lost.'
        }
      />
      <Claim
        established={availability.formatPreviewable}
        label="This format can be previewed here"
        note={
          availability.formatPreviewable
            ? null
            : 'OMNΛS does not render this file type.'
        }
      />
    </ul>
  </div>
);

export const DocumentRecordDetails = ({ document: doc, onDownload }) => {
  if (!doc) {
    return (
      <div
        data-testid="document-context-empty"
        className="px-4 py-10 text-center text-muted-foreground"
      >
        <Circle
          className="mx-auto mb-3 h-6 w-6 opacity-40"
          aria-hidden="true"
        />
        <p className="text-sm">No document selected</p>
        <p className="mt-1 text-xs">Select a document to see its record.</p>
      </div>
    );
  }

  return (
    <div data-testid="document-record-details" className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
          <DocumentTypeIcon iconKey={doc.iconKey} />
        </span>
        <div className="min-w-0">
          <h3 className="break-words text-sm font-semibold leading-snug text-foreground">
            {doc.name}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusMark display={doc.statusDisplay} />
            <StatusMark display={doc.reviewStatusDisplay} />
          </div>
        </div>
      </div>

      {doc.description && (
        <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground">
          {doc.description}
        </p>
      )}

      <dl className="divide-y divide-border/60 border-y border-border/60">
        <Field label="Category">{doc.categoryLabel}</Field>
        <Field label="File type" mono>
          {doc.fileTypeLabel}
        </Field>
        <Field label="Size">{doc.fileSizeLabel}</Field>
        <Field label="Version" mono>
          {doc.versionLabel}
        </Field>
        <Field label="Uploaded">{formatTimestamp(doc.createdAt)}</Field>
        <Field label="Updated">{formatTimestamp(doc.updatedAt)}</Field>
        {/*
          The contract returns an uploader ID and no name. Showing the id is
          honest; inventing a display name from it would not be.
        */}
        <Field label="Uploaded by (user id)" mono>
          {doc.uploadedById || 'Not stated'}
        </Field>
      </dl>

      <AvailabilityBreakdown availability={doc.availability} />

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!doc.downloadUrl}
          onClick={() => onDownload?.(doc)}
          data-testid="context-download-btn"
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </Button>
        {doc.downloadUrl && (
          <Button
            variant="ghost"
            size="sm"
            asChild
            data-testid="context-open-btn"
          >
            <a href={doc.downloadUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              Open
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};

export default DocumentRecordDetails;
